import * as XLSX from 'xlsx';

// Helper function to convert base64 Excel to workbook and extract sheets
export const readBase64Excel = (base64Content) => {
  try {
    // Decode base64 string to binary
    const binaryString = window.atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Read the Excel file using XLSX
    const workbook = XLSX.read(bytes, { type: 'array' });
    
    return workbook;
  } catch (error) {
    console.error('Error reading Excel file:', error);
    return null;
  }
};

export const parseJsonResponse = (response) => {
  if (typeof response === 'object') {
    // If it's already an object, recursively clean NaN values
    return JSON.parse(JSON.stringify(response, (key, value) => {
      if (typeof value === 'number' && isNaN(value)) {
        return null;
      }
      return value;
    }));
  }

  if (typeof response !== 'string') {
    return null;
  }

  try {
    // Clean the JSON string by removing potential BOM and other invisible characters
    let cleanJsonString = response.trim()
      .replace(/^\uFEFF/, '') // Remove BOM
      .replace(/^\u200B/, '') // Remove zero-width space
      .replace(/\u0000/g, '') // Remove null characters
      .replace(/\u2028/g, '\\n') // Replace line separator
      .replace(/\u2029/g, '\\n') // Replace paragraph separator
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, ''); // Remove control characters
    
    // Try to find the actual JSON part if it's wrapped in other content
    const jsonStart = cleanJsonString.indexOf('{');
    const jsonEnd = cleanJsonString.lastIndexOf('}') + 1;
    
    if (jsonStart !== -1 && jsonEnd > jsonStart) {
      cleanJsonString = cleanJsonString.substring(jsonStart, jsonEnd);
    }
    
    // Handle NaN values by replacing them with null before parsing
    // This handles various patterns of NaN values
    cleanJsonString = cleanJsonString
      .replace(/:\s*NaN\s*([,}])/g, ': null$1') // Replace : NaN with : null
      .replace(/:\s*-?NaN\s*([,}])/g, ': null$1') // Replace : -NaN or : -NaN with : null
      .replace(/NaN([,}\]])/g, 'null$1') // Handle NaN without colon
      .replace(/,(\s*[}\]])/g, '$1'); // Remove trailing commas

    // Parse the cleaned JSON
    const parsed = JSON.parse(cleanJsonString);
    
    // After parsing, recursively replace any NaN values in the parsed object
    return JSON.parse(JSON.stringify(parsed, (key, value) => {
      if (typeof value === 'number' && isNaN(value)) {
        return null;
      }
      return value;
    }));
  } catch (error) {
    console.error("Error parsing JSON response:", error);
    console.error("Response snippet:", response.substring(0, 500));
    return null;
  }
};

export const extractSettingsData = (response) => {
  const parsedResponse = parseJsonResponse(response);
  
  if (!parsedResponse || typeof parsedResponse !== 'object') {
    return null;
  }
  // Try different possible response structures
  if (parsedResponse.simulation?.settings) {
    return parsedResponse.simulation.settings;
  } else if (parsedResponse.settings) {
    return parsedResponse.settings;
  } else if (parsedResponse.simulation) {
    // Check if simulation itself contains the settings
    const hasConfigKeys = Object.keys(parsedResponse.simulation).some(key => 
      key === 'no BESS' || key.includes('MWh') || key.includes('MW')
    );
    if (hasConfigKeys) {
      return parsedResponse.simulation;
    }
  }

  return null;
};

export const extractKpiData = (response) => {
  const parsedResponse = parseJsonResponse(response);
  
  if (!parsedResponse || typeof parsedResponse !== 'object') {
    return null;
  }

  return parsedResponse.simulation?.kpis || null;
};


export const parseBessConfig = (configName) => {
  if (configName === 'no BESS') {
    return { power: '0', energy: '0' };
  }
  
  // Parse "4MWh / 1MW" format
  const match = configName.match(/(\d+(?:\.\d+)?)MWh\s*\/\s*(\d+(?:\.\d+)?)MW/);
  if (match) {
    return { power: match[2], energy: match[1] };
  }
  
  return { power: 'N/A', energy: 'N/A' };
};

export const categorizeSetting = (key) => {
  let category = 'Other';
  let displayKey = key;
  
  if (key.startsWith('grid_')) {
    category = 'Grid Connection';
    displayKey = key.substring('grid_'.length);
  } else if (key.startsWith('pv_')) {
    category = 'PV Configuration';
    displayKey = key.substring('pv_'.length);
  } else if (key.startsWith('simulation_config_')) {
    category = 'Simulation Config';
    displayKey = key.substring('simulation_config_'.length);
  } else if (key.startsWith('electricity_consumption_')) {
    category = 'Electricity Consumption';
    displayKey = key.substring('electricity_consumption_'.length);
  } else if (key.startsWith('electricity_market_')) {
    category = 'Electricity Market';
    displayKey = key.substring('electricity_market_'.length);
  } else if (key.startsWith('battery_energy_storage_system_')) {
    category = 'Battery Energy Storage System';
    displayKey = key.substring('battery_energy_storage_system_'.length);
  } else if (key.startsWith('financial_')) {
    category = 'Financial';
    displayKey = key.substring('financial_'.length);
  }

  return { category, displayKey };
};


export const formatDisplayValue = (value) => {
  if (Array.isArray(value)) {
    return `[${value.length} items]`;
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  if (typeof value === 'number') {
    return isNaN(value) ? 'N/A' : value.toLocaleString();
  }
  
  // Check if value is a date string and format it consistently
  if (typeof value === 'string') {
    // Check for ISO 8601 format (2024-07-31T22:00:00.000Z)
    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (isoDateMatch) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        // Format as: YYYY-MM-DD HH:MM:SS
        return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
      }
    }
    
    // Check for other date formats (2025-10-1 00:00:00)
    const altDateMatch = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})/);
    if (altDateMatch) {
      const [_, year, month, day, hour, minute, second] = altDateMatch;
      // Normalize to consistent format: YYYY-MM-DD HH:MM:SS
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} ${hour}:${minute}:${second}`;
    }
  }
  
  return value ?? '-';
};


export const createKpiSheet = (kpiData) => {
  if (!kpiData || Object.keys(kpiData).length === 0) {
    return null;
  }

  // Filter out "no BESS" configuration from kpiData
  const filteredKpiData = Object.fromEntries(
    Object.entries(kpiData).filter(([key]) => key !== 'no BESS')
  );

  if (Object.keys(filteredKpiData).length === 0) {
    return null;
  }

  // Only show "Monthly savings" KPI
  const sortedKpiNames = ['Monthly savings'];
  
  // Get all periods from the first configuration
  const firstConfig = Object.keys(filteredKpiData)[0];
  const allPeriods = Object.keys(filteredKpiData[firstConfig]);
  const sortedPeriods = allPeriods.sort((a, b) => {
    if (a === 'aggregated') return -1;
    if (b === 'aggregated') return 1;
    return a.localeCompare(b);
  });

  // Create header structure
  const configNames = Object.keys(filteredKpiData);
  const bessConfigs = configNames.map(parseBessConfig);

  const headerRow1 = ['', ...configNames.flatMap(config => [config, ...Array(sortedKpiNames.length - 1).fill('')])];
  const headerRow2 = ['BESS power', ...configNames.flatMap((config, index) => {
    const powerValue = bessConfigs[index].power;
    const numPower = powerValue !== 'N/A' && !isNaN(powerValue) ? parseFloat(powerValue) : powerValue;
    return [numPower, ...Array(sortedKpiNames.length - 1).fill('')];
  })];
  const headerRow3 = ['BESS energy', ...configNames.flatMap((config, index) => {
    const energyValue = bessConfigs[index].energy;
    const numEnergy = energyValue !== 'N/A' && !isNaN(energyValue) ? parseFloat(energyValue) : energyValue;
    return [numEnergy, ...Array(sortedKpiNames.length - 1).fill('')];
  })];
  const headerRow4 = ['Market', ...configNames.flatMap(() => sortedKpiNames)];

  const kpiSheetData = [headerRow1, headerRow2, headerRow3, headerRow4];

  // Add data rows for each period
  sortedPeriods.forEach(period => {
    const row = [period];
    configNames.forEach(config => {
      const kpis = filteredKpiData[config][period] || {};
      sortedKpiNames.forEach(kpiName => {
        const kpiValue = kpis[kpiName];
        if (kpiValue === null || kpiValue === undefined) {
          row.push('N/A');
        } else if (typeof kpiValue === 'number') {
          // Format IRR as percentage
          if (kpiName === 'IRR') {
            row.push((kpiValue * 100).toFixed(2));
          } else {
            row.push(kpiValue);
          }
        } else {
          row.push(kpiValue);
        }
      });
    });
    kpiSheetData.push(row);
  });

  const kpiWs = XLSX.utils.aoa_to_sheet(kpiSheetData);
  
  // Set column widths for better readability
  const colWidths = [
    { wch: 15 }, // Period column
    ...configNames.flatMap(() => sortedKpiNames.map(() => ({ wch: 18 }))) // KPI columns for each config
  ];
  kpiWs['!cols'] = colWidths;

  // Merge cells for configuration headers
  const merges = [];
  let colIndex = 1; // Start after Period column
  configNames.forEach(config => {
    if (sortedKpiNames.length > 1) {
      merges.push({
        s: { r: 0, c: colIndex },
        e: { r: 0, c: colIndex + sortedKpiNames.length - 1 }
      });
    }
    colIndex += sortedKpiNames.length;
  });
  kpiWs['!merges'] = merges;

  return kpiWs;
};


export const createSettingsSheet = (settingsData) => {
  if (!settingsData || Object.keys(settingsData).length === 0) {
    return null;
  }

  const settingsSheetData = [];
  
  // Add header
  settingsSheetData.push(['Simulation Settings']);
  settingsSheetData.push(['Configuration', 'Category', 'Setting', 'Value']);
  settingsSheetData.push([]);

  // Process settings data
  Object.entries(settingsData).forEach(([config, periods]) => {
    // Use aggregated data if available, otherwise use the first period
    const aggregatedData = periods.aggregated || periods[Object.keys(periods).find(p => p !== 'aggregated')] || {};
    
    // Group settings by category
    const groupedSettings = {};
    Object.entries(aggregatedData).forEach(([key, value]) => {
      // Skip null, undefined, or NaN values
      if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
        return;
      }
      
      const { category, displayKey } = categorizeSetting(key);

      if (!groupedSettings[category]) {
        groupedSettings[category] = [];
      }
      groupedSettings[category].push({ key: displayKey, fullKey: key, value });
    });

    // Add settings data to sheet
    Object.entries(groupedSettings).sort(([a], [b]) => a.localeCompare(b)).forEach(([category, settings]) => {
      settings.forEach(({ key, fullKey, value }) => {
        const displayValue = formatDisplayValue(value);
        
        settingsSheetData.push([
          config,
          category,
          key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          displayValue
        ]);
      });
    });
  });

  const settingsWs = XLSX.utils.aoa_to_sheet(settingsSheetData);
  
  // Set column widths
  settingsWs['!cols'] = [
    { wch: 25 }, // Configuration
    { wch: 30 }, // Category
    { wch: 40 }, // Setting
    { wch: 30 }  // Value
  ];

  return settingsWs;
};


export const exportSimulationsToExcel = async (
  selectedSimulations,
  fetchKpis,
  fetchSettings,
  fetchInputFile,
  setSnackbarMessage,
  setSnackbarOpen
) => {
  if (selectedSimulations.length === 0) {
    setSnackbarMessage('Please select at least one simulation to export');
    setSnackbarOpen(true);
    return;
  }

  setSnackbarMessage(`Exporting ${selectedSimulations.length} simulation(s)...`);
  setSnackbarOpen(true);

  // Export each simulation as a separate file
  for (let i = 0; i < selectedSimulations.length; i++) {
    const simulation = selectedSimulations[i];
    
    try {
      // Load KPIs and Settings for current simulation
      const responseKpis = await fetchKpis(simulation.event_id);
      const responseSettings = await fetchSettings(simulation.event_id);
      
      const wb = XLSX.utils.book_new();
      
      // Create KPI data sheet
      const kpiData = extractKpiData(responseKpis);
      if (kpiData) {
        const kpiWs = createKpiSheet(kpiData);
        if (kpiWs) {
          XLSX.utils.book_append_sheet(wb, kpiWs, "KPI Data");
        }
      }

      // Create Settings data sheet
      const settingsData = extractSettingsData(responseSettings);
      if (settingsData) {
        const settingsWs = createSettingsSheet(settingsData);
        if (settingsWs) {
          XLSX.utils.book_append_sheet(wb, settingsWs, "Settings Data");
        }
      }

      // Try to fetch and add input file sheets, but don't fail if it doesn't exist
      try {
        const responseInputFile = await fetchInputFile(simulation.event_id);
        
        // Parse the response to get the base64 content
        let inputFileData = null;
        if (typeof responseInputFile === 'string') {
          try {
            inputFileData = JSON.parse(responseInputFile);
          } catch (e) {
            console.warn("Could not parse input file response:", e);
          }
        } else if (typeof responseInputFile === 'object') {
          inputFileData = responseInputFile;
        }
        
        // Add input file sheets to the main workbook
        if (inputFileData && inputFileData.content) {
          const inputWorkbook = readBase64Excel(inputFileData.content);
          
          if (inputWorkbook && inputWorkbook.SheetNames) {
            // Add all sheets from input file to the main workbook
            inputWorkbook.SheetNames.forEach((sheetName, index) => {
              const sheet = inputWorkbook.Sheets[sheetName];
              // Prefix sheet name with "Input - " to distinguish from KPI/Settings sheets
              let newSheetName = `Input - ${sheetName}`;
              
              // Truncate if too long (Excel has 31 char limit)
              if (newSheetName.length > 31) {
                newSheetName = `Input ${index + 1}`;
              }
              
              XLSX.utils.book_append_sheet(wb, sheet, newSheetName);
            });
          }
        }
      } catch (error) {
        console.warn(`Input file not found for simulation ${simulation.event_id}:`, error.message);
        // Continue with export even if input file is missing
      }
      //Object { created: "2025-10-13T09:00:50Z", event_id: "a015475f-8028-46f7-a2a6-34b9e955f9e1", ttl: 1785511542, message: '{"name": "test", "status": "Complet
      let messageData = JSON.parse(simulation.message);
      // Write the Excel file for this simulation
      const safeName = (messageData.name || simulation.event_id).replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${safeName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

    } catch (error) {
      console.error(`Error exporting simulation ${simulation.event_id}:`, error);
      setSnackbarMessage(`Error exporting ${simulation.name || simulation.event_id}: ${error.message}`);
      setSnackbarOpen(true);
    }
  }

  setSnackbarMessage(`Successfully exported ${selectedSimulations.length} simulation(s)!`);
  setSnackbarOpen(true);
};
